import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  FormDialogMode,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import {
  Button,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { Delete, Edit, Visibility } from '@material-ui/icons'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoCalculoConcepto } from '../../../../../api'
import { EnumClientTypes } from '../../../../../api/Argentia/enums/enumClientTypes'
import { EnumConceptType } from '../../../../../api/Argentia/enums/enumConceptType'
import {
  ConceptModel,
  ProductConceptModel,
  ProductModel
} from '../../../../../api/Argentia/model/contracts'
import {
  EnumTipoCondicionConcepto,
  EnumTipoRelacionProductoConcepto
} from '../../../../../api/oData/enums'
import { CardWithTransition } from '../../../../../components/shared/cardWithTransition/CardWithTransition'
import { LoadingCard } from '../../../../../components/shared/loadingCard/loadingCard'
import { enumToSelectOptionArray, enumTranslateOption } from '../../../../../helpers'
import { getUserPermissions } from '../../../../../helpers/permissionsHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../../hooks'
import { Pages } from '../../../../../model/RBAC/constants'
import { getActiveCompany, getCustomerDomain } from '../../../../../redux'
import { filterConceptsByRelationType } from '../../../products.mapper'
import { fetchConcepts } from '../../../products.service'
import { CardEmptyContent } from '../shared/cardEmptyContent/cardEmptyContent'
import {
  mapConceptsResponse,
  onAddMapConceptModelToProductConceptModel,
  onEditMapConceptModelToProductConceptModel
} from './conceptCard.mapper'
import { styles } from './conceptCard.styles'

export interface ConceptsCardProps {
  filterByRelationType: EnumTipoRelacionProductoConcepto
  productDetail: Partial<ProductModel> | undefined
  clientType: EnumClientTypes
  // concepts: ProductConceptModel[] | undefined
  // totalConcepts: ProductConceptModel[] | undefined
  onAddConceptsRowValues?: (selectedRowsData: ProductConceptModel[]) => void
  onEditConceptsRowValues?: (formData: ProductConceptModel) => void
  onDeleteConceptsRowValues?: (conceptId: number, tempId: string | undefined) => void
  onViewConceptDetail?: (rowData: ProductConceptModel) => void
  isFetching: boolean
}

export const ConceptCard: React.FC<ConceptsCardProps> = ({
  filterByRelationType,
  productDetail,
  clientType,
  onAddConceptsRowValues,
  onEditConceptsRowValues,
  onDeleteConceptsRowValues,
  onViewConceptDetail,
  isFetching
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'productsEdit', 'enums', 'dataTable'])
  const { info, error } = useNotify()
  const currency = productDetail?.currency ? productDetail.currency : undefined
  const commodity = productDetail?.commodity ? productDetail.commodity : undefined
  const companyId = productDetail?.companyId ? productDetail.companyId : getActiveCompany()?.id
  const { openForm, closeForm, updateFormDefinition, formProps, CustomFormDialog } = useFormDialog()
  const [confirmationDialogIsOpen, setConfirmationDialogIsOpen] = useState<boolean>(false)
  const [conceptToDelete, setConceptToDelete] = useState<ProductConceptModel>()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [conceptIsLoading, setConceptIsLoading] = useState<boolean>(false)
  const [conceptsAvailable, setConceptsAvailable] = useState<ConceptModel[]>([])

  const [conditionTypeSelectedForBulk, setConditionTypeSelectedForBulk] =
    useState<EnumTipoCondicionConcepto | null>(null)

  const [selectedRowsData, setSelectedRowsData] = useState<ProductConceptModel[]>([])

  // Import Styles
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const concepts = productDetail?.relatedConcepts
  const cardTitle =
    filterByRelationType === EnumTipoRelacionProductoConcepto.Obligatorio
      ? t('productsEdit:components.conceptsCard.obligatoryTitle')
      : filterByRelationType === EnumTipoRelacionProductoConcepto.Opcional
        ? t('productsEdit:components.conceptsCard.optionalTitle')
        : t('productsEdit:components.conceptsCard.conditionalTitle')

  //#region Handlers Starts

  const onAddMassiveConceptHandler = selectedRows => {
    // Map Concepts as ProductConcepts (consider different if is required / optional)
    const newProductConcepts = onAddMapConceptModelToProductConceptModel(
      selectedRows,
      filterByRelationType,
      conditionTypeSelectedForBulk
    )
    // callBack to handle state & closeDialog
    onAddConceptsRowValues && onAddConceptsRowValues(newProductConcepts)
    handleDialogClose()
  }

  const onEditConceptHandler = async rowData => {
    openForm({
      definition: conceptEditFormDefinition,
      title: t('productsEdit:components.conceptsCard.forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: rowData,
      onClose: onEditConceptCloseHandler
    })

    return true
  }

  const onEditConceptCloseHandler = formData => {
    if (formData) {
      // Check if dates are valid
      const startDateValid =
        !formData.endDate || moment(formData.startDate).isSameOrBefore(formData.endDate, 'day')
      if (!startDateValid) {
        error(t('productsEdit:components.conceptsCard.forms.edit.notifications.datesInvalid'))
        return
      }
      // Map new edited concept and callback to update state
      const newProductConcept = onEditMapConceptModelToProductConceptModel(formData)
      onEditConceptsRowValues && onEditConceptsRowValues(newProductConcept)
    }

    closeForm()
  }

  const onDeleteConceptHandler = async rowData => {
    if (!onDeleteConceptsRowValues) return false

    // if selectedRow did not fail
    if (rowData) {
      // confirm that data is deletable
      if (rowData.conceptNotFromApiGet === true) {
        onDeleteConceptsRowValues(rowData.conceptId, rowData.tempId)
      } else {
        // set concept in separate state to handle confirmation Dialog
        setConceptToDelete({ ...rowData })
        setConfirmationDialogIsOpen(true)
      }
    } else {
      error(t('productsEdit:components.conceptsCard.delete'))
    }
    return true
  }

  const onSelectConditionTypeFormHandler = async () => {
    openForm({
      definition: selectConditionTypeFormDefinition,
      title: t('productsEdit:components.conceptsCard.forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      // inputData: ,
      onClose: onSelectConditionTypeFormCloseHandler
    })

    return true
  }

  const onSelectConditionTypeFormCloseHandler = formData => {
    if (formData) {
      handleDialogOpen()
    }
    closeForm()
  }

  //#region Dialog Handlers Starts

  const onClickAddConceptHandler = async () => {
    if (!onAddConceptsRowValues) return false

    filterByRelationType === EnumTipoRelacionProductoConcepto.Condicional
      ? handleDialogOpenForConditionalConcept()
      : handleDialogOpen()
    return true
  }

  const handleDialogOpen = async () => {
    setIsOpen(true)
    fetchAvailableConcepts()
    return true
  }

  const handleDialogOpenForConditionalConcept = async () => {
    onSelectConditionTypeFormHandler()
    return true
  }

  const handleDialogClose = () => {
    setConditionTypeSelectedForBulk(null)
    setIsOpen(false)
  }

  //#endregion Dialog Handlers Ends

  //#region confirmationDialog Handlers Starts

  const confirmationDialogHandleClose = () => {
    setConfirmationDialogIsOpen(false)
  }

  const confirmationDialogHandleDelete = () => {
    setConfirmationDialogIsOpen(false)
    conceptToDelete &&
      onDeleteConceptsRowValues &&
      onDeleteConceptsRowValues(conceptToDelete.conceptId, conceptToDelete.tempId)
    setConceptToDelete(undefined) // clean aux state after deletion
  }

  //#region confirmationDialog Handlers Ends

  //#endregion Handlers Ends

  //#region Form Definition Starts

  const conceptEditFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_CONCEPT',
        sections: [
          {
            inputs: [
              {
                name: 'code',
                label: t('productsEdit:components.conceptsCard.forms.edit.inputs.code'),
                type: DataType.TEXT,
                readOnly: true
              },
              {
                name: 'description',
                label: t('productsEdit:components.conceptsCard.forms.edit.inputs.description'),
                type: DataType.TEXT,
                readOnly: true
              },
              {
                name: 'startDate',
                label: t('productsEdit:components.conceptsCard.forms.edit.inputs.startDate'),
                type: DataType.DATE
              },
              {
                name: 'endDate',
                label: t('productsEdit:components.conceptsCard.forms.edit.inputs.endDate'),
                type: DataType.DATE
              },
              {
                name: 'conditionType',
                label: t('productsEdit:components.conceptsCard.forms.edit.inputs.conditionType'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumTipoCondicionConcepto,
                  'enums:EnumTipoCondicionConcepto'
                ),
                display: filterByRelationType === EnumTipoRelacionProductoConcepto.Condicional
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

  const selectConditionTypeFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD_CONDITIONAL_CONCEPT',
        sections: [
          {
            inputs: [
              {
                name: 'conditionType',
                label: t(
                  'productsEdit:components.conceptsCard.conditionTypeForm.inputs.conditionType'
                ),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumTipoCondicionConcepto,
                  'enums:EnumTipoCondicionConcepto'
                ),
                required: true,
                onChange: value => {
                  setConditionTypeSelectedForBulk(value as EnumTipoCondicionConcepto)
                }
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

  //#endregion Form Definition Ends

  //#region DataTable Config Starts

  const getRowCustomActions = () => {
    const actions: any[] = []

    if (onViewConceptDetail) {
      actions.push({
        icon: <Visibility fontSize="small" />,
        tooltip: t('productsEdit:components.conceptsCard.dataTable.actions.viewDetail'),
        onClick: (rowData: ProductConceptModel) => onViewConceptDetail(rowData),
        userPermission: UserPermission.EDIT
      })
    }

    if (onEditConceptsRowValues) {
      actions.push({
        icon: <Edit fontSize="small" />,
        tooltip: t('productsEdit:components.conceptsCard.dataTable.actions.edit'),
        onClick: onEditConceptHandler,
        userPermission: UserPermission.EDIT
      })
    }

    if (onDeleteConceptsRowValues) {
      actions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t('productsEdit:components.conceptsCard.dataTable.actions.delete'),
        onClick: onDeleteConceptHandler,
        userPermission: UserPermission.EDIT
      })
    }

    return actions
  }

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'code',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'conceptType',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.conceptType'),
      selectOptions: enumToSelectOptionArray(EnumConceptType, 'enums:EnumTipoConcepto'),
      type: DataType.SELECT
    },
    {
      name: 'calculationType',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.calculationType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTipoCalculoConcepto,
        'enums:EnumTipoCalculoConcepto'
      )
    },
    // {
    //   name: 'quantityType',
    //   label: t('productsEdit:components.conceptsCard.dataTable.columns.quantityType'),
    //   type: DataType.TEXT
    // },
    {
      name: 'startDate',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'conditionType',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.conditionType'),
      selectOptions: enumToSelectOptionArray(
        EnumTipoCondicionConcepto,
        'enums:EnumTipoCondicionConcepto'
      ),
      type: DataType.SELECT,
      display: filterByRelationType === EnumTipoRelacionProductoConcepto.Condicional
    }
  ]

  const dataTableOptions: DataTableOptions = {
    analytics: {
      customer: getCustomerDomain(),
      view: t('productsEdit:components.conceptsCard.dataTable.analytics')
    },
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: !!onAddConceptsRowValues,
    customInsertEditModal: true,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    onRowInsert: onAddConceptsRowValues ? onClickAddConceptHandler : undefined,
    rowCustomActions: getRowCustomActions()
  }

  const dialogTableColumns: DataTableColumn[] = [
    {
      name: 'code',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'conceptType',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.conceptType'),
      selectOptions: enumToSelectOptionArray(EnumConceptType, 'enums:EnumTipoConcepto'),
      type: DataType.SELECT
    },
    {
      name: 'calculationType',
      label: t('productsEdit:components.conceptsCard.dataTable.columns.calculationType'),
      selectOptions: enumToSelectOptionArray(
        EnumTipoCalculoConcepto,
        'enums:EnumTipoCalculoConcepto'
      ),
      type: DataType.SELECT
    }
  ]

  const dialogTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    analytics: {
      customer: getCustomerDomain(),
      view: t('productsEdit:components.conceptsCard.dialog.dataTable.analytics')
    },
    search: true,
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    customInsertEditModal: false,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    selectableRows: true
  }
  //#endregion DataTable Config Ends

  //#region Functions Starts

  const fetchAvailableConcepts = async () => {
    setConceptIsLoading(true)
    currency &&
      companyId &&
      (await fetchConcepts(currency, commodity, companyId, clientType)
        .then(response => {
          if (response.success) {
            const conceptsResponse = filterAddedConcepts(
              mapConceptsResponse(response.data),
              concepts
            )
            setConceptsAvailable(conceptsResponse)
          } else {
            error(t('productsEdit:components.conceptsCard.dialog.notifications.load.error'))
          }
        })
        .finally(() => setConceptIsLoading(false)))
  }

  // filter concepts from those already used
  const filterAddedConcepts = (
    concepts: ConceptModel[],
    conceptsToFilter: ProductConceptModel[] | undefined
  ) => {
    if (!conceptsToFilter) {
      return concepts
    } else {
      return concepts.filter(
        concept => !conceptsToFilter.some(item => item.conceptId === concept.conceptId)
      )
    }
  }

  const getAddConceptDialogDataTableTitle = () => {
    if (!conditionTypeSelectedForBulk) {
      return t('productsEdit:components.conceptsCard.dialog.dataTable.title')
    }

    switch (filterByRelationType) {
      case EnumTipoRelacionProductoConcepto.Condicional: {
        return t('productsEdit:components.conceptsCard.dialog.dataTable.titleConditional', {
          conditionType: enumTranslateOption(
            conditionTypeSelectedForBulk,
            EnumTipoCondicionConcepto,
            'enums:EnumTipoCondicionConcepto'
          )
        })
      }
      default: {
        return t('productsEdit:components.conceptsCard.dialog.dataTable.title')
      }
    }
  }

  //#endregion Functions Ends

  //#region Hooks Starts

  //#endregion Hooks Ends

  if (isFetching) {
    return <LoadingCard />
  }

  const conceptsList = filterConceptsByRelationType(concepts, filterByRelationType)

  return (
    <>
      <CardWithTransition show={true} raised>
        <CardHeader
          title={cardTitle}
          titleTypographyProps={{ variant: 'h2', color: 'secondary' }}
        />
        {conceptsList?.length === 0 ? (
          <CardEmptyContent title={t('productsEdit:components.conceptsCard.emptyCard.title')}>
            {onAddConceptsRowValues && (
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={onClickAddConceptHandler}
              >
                {t('productsEdit:components.conceptsCard.emptyCard.buttonLabel')}
              </Button>
            )}
          </CardEmptyContent>
        ) : (
          <CardContent className={classes.cardContent}>
            <DataTable
              className={classes.dataTableStyle}
              data={conceptsList}
              columns={dataTableColumns}
              options={dataTableOptions}
              userPermissions={getUserPermissions(Pages.Client)}
            />
          </CardContent>
        )}
      </CardWithTransition>

      <Dialog
        fullWidth
        maxWidth={'lg'}
        disableBackdropClick
        disableEscapeKeyDown
        open={isOpen}
        onClose={handleDialogClose}
      >
        <DialogContent>
          {conceptIsLoading ? (
            <>
              <DialogTitle disableTypography>
                <Typography variant="h5" color="secondary" noWrap>
                  <b>{t('productsEdit:components.conceptsCard.dialog.title')}</b>
                </Typography>
              </DialogTitle>
              <DataTableSkeleton rows={7} elevation={0} />
            </>
          ) : (
            <DataTable
              title={getAddConceptDialogDataTableTitle()}
              subtitle={t('productsEdit:components.conceptsCard.dialog.dataTable.subtitle')}
              className={classes.dataTableStyle}
              data={conceptsAvailable}
              columns={dialogTableColumns}
              options={dialogTableOptions}
              onSelectionChange={(selectedRowsData, _) => {
                setSelectedRowsData(selectedRowsData)
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={handleDialogClose} color="secondary">
            {t('productsEdit:components.conceptsCard.dialog.actions.button.backLabel')}
          </Button>
          <Button
            size="small"
            onClick={() => onAddMassiveConceptHandler(selectedRowsData)}
            color="primary"
          >
            {t('productsEdit:components.conceptsCard.dialog.actions.button.saveLabel')}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomFormDialog {...formProps} />
      <Dialog open={confirmationDialogIsOpen} onClose={confirmationDialogHandleClose}>
        <DialogTitle>
          {t('productsEdit:components.conceptsCard.confirmationDialog.title')}
        </DialogTitle>
        <DialogContent>
          {t('productsEdit:components.conceptsCard.confirmationDialog.content')}
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmationDialogHandleClose}>
            {t('productsEdit:components.conceptsCard.confirmationDialog.actions.button.backLabel')}
          </Button>
          <Button onClick={confirmationDialogHandleDelete}>
            {t('productsEdit:components.conceptsCard.confirmationDialog.actions.button.okLabel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
